import React from 'react';
import { Question, QuestionOption } from 'types';
import { cn } from 'lib/utils';
import { Icon } from '../icons/Icon';
import { Textarea } from '../ui/textarea';
import {
  getMultipleSelectLimits,
  getMultipleSelectRequirementText,
} from '../../utils/multipleSelectLimits';
import { QuestionStatusLine, SelectionCheck } from './QuestionStatusLine';

interface Props {
  question: Question;
  selectedOptions: number[];
  onChange: (optionIds: number[]) => void;
  customOtherValue?: string;
  onCustomOtherChange?: (value: string) => void;
}

export const hasRenderableMultipleSelectLabel = (
  option: QuestionOption | null | undefined,
): option is QuestionOption =>
  typeof option?.front_text === 'string' && option.front_text.trim().length > 0;

export const MultipleSelectQuestion: React.FC<Props> = ({
  question,
  selectedOptions,
  onChange,
  customOtherValue = '',
  onCustomOtherChange = () => {},
}) => {
  const [blockedOptionId, setBlockedOptionId] = React.useState<number | null>(null);
  const { minSelections, maxSelections } = getMultipleSelectLimits(question.metadata);
  const atMax = typeof maxSelections === 'number' && selectedOptions.length >= maxSelections;
  const status = getMultipleSelectStatus(question, selectedOptions, customOtherValue, {
    minSelections,
    maxSelections,
  });

  const toggleOption = (optionId: number) => {
    if (selectedOptions.includes(optionId)) {
      setBlockedOptionId(null);
      onChange(selectedOptions.filter((id) => id !== optionId));
      return;
    }

    if (atMax) {
      setBlockedOptionId(optionId);
      return;
    }

    setBlockedOptionId(null);
    onChange([...selectedOptions, optionId]);
  };

  return (
    <div className="w-full flex flex-col gap-2 self-stretch">
      {(question.options ?? [])
        .filter(hasRenderableMultipleSelectLabel)
        .map((option) => {
          const optionId = option.id ?? -1;
          const isSelected = selectedOptions.includes(optionId);
          const isOther = option.is_other && isSelected;

          return (
            <div key={option.id} className="w-full space-y-2">
              <SelectionCheckbox
                isSelected={isSelected}
                aria-disabled={atMax && !isSelected ? true : undefined}
                className={cn(atMax && !isSelected && 'opacity-60')}
                onToggle={() => toggleOption(optionId)}
              >
                {option.front_text.trim()}
              </SelectionCheckbox>

              {blockedOptionId === optionId ? (
                <p role="alert" className="pl-1 text-sm text-destructive">
                  {`You can select up to ${maxSelections} option${maxSelections === 1 ? '' : 's'}. Deselect one before choosing another.`}
                </p>
              ) : null}

              {isOther && (
                <div className="flex flex-row items-center gap-2">
                  <Icon name="downRightArrow" size={32} className="p-1 -mt-2" />
                  <Textarea
                    className="text-base"
                    placeholder="Type your own answer"
                    aria-label="Your own answer"
                    value={customOtherValue}
                    onChange={(event) => onCustomOtherChange(event.target.value)}
                    required
                    autoFocus
                  />
                </div>
              )}
            </div>
          );
        })}
      {status ? <QuestionStatusLine ready={status.ready}>{status.text}</QuestionStatusLine> : null}
    </div>
  );
};

type MultipleSelectStatus = { ready: boolean; text: string };

const getMultipleSelectStatus = (
  question: Question,
  selectedOptions: number[],
  customOtherValue: string,
  { minSelections, maxSelections }: { minSelections?: number; maxSelections?: number },
): MultipleSelectStatus | null => {
  const count = selectedOptions.length;
  const otherOption = (question.options ?? []).find((option) => option.is_other);
  const otherNeedsText =
    Boolean(otherOption && selectedOptions.includes(otherOption.id)) && customOtherValue.trim().length === 0;

  if (otherNeedsText) {
    return { ready: false, text: 'Type your own answer, or unselect it.' };
  }
  if (count === 0) {
    const requirement = getMultipleSelectRequirementText(question.metadata);
    return requirement ? { ready: false, text: requirement } : null;
  }
  if (typeof maxSelections === 'number' && count > maxSelections) {
    return { ready: false, text: `${count} selected. Select up to ${maxSelections}.` };
  }
  if (typeof minSelections === 'number' && count < minSelections) {
    return { ready: false, text: `${count} of at least ${minSelections} selected.` };
  }
  if (typeof maxSelections === 'number') {
    return { ready: true, text: `${count} of ${maxSelections} selected.` };
  }
  return { ready: true, text: `${count} selected.` };
};

export interface SelectionCheckboxProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isSelected: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}


export const SelectionCheckbox = React.forwardRef<
  HTMLButtonElement,
  SelectionCheckboxProps
>(({ className, isSelected, onToggle, children, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onToggle}
      aria-pressed={isSelected}
      className={cn(
        "w-full flex items-center gap-3 px-4 py-3 bg-white rounded-lg border shadow-elevation-sm relative overflow-hidden transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2",
        isSelected
          ? "border-foundation-orange-500 shadow-[0_0_0_1px_var(--foundation-orange-500)]"
          : "border-transparent hover:bg-gray-50",
        className
      )}
      {...props}
    >
      <SelectionCheck checked={isSelected} />

      {/* Text label */}
      <div className="flex-1 text-left leading-[var(--body-m-line-height)]">
        <div className={cn("type-body-m text-foundation-base-darkbase-dark-500", isSelected && "font-semibold")}>
          {children}
        </div>
      </div>
    </button>
  );
});
